import React, { useEffect, useState } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { XMarkIcon, SparklesIcon, BugAntIcon, ChatBubbleBottomCenterTextIcon } from '@heroicons/react/24/outline';

const WELCOME_MODAL_KEY = 'neoface-welcome-shown';

interface WelcomeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const WelcomeModal: React.FC<WelcomeModalProps> = ({ isOpen, onClose }) => {
  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-40">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-40 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-2xl sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            {/* Close button */}
            <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                <span className="sr-only">Close</span>
                <XMarkIcon aria-hidden="true" className="h-6 w-6" />
              </button>
            </div>

            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100 sm:mx-0 sm:h-10 sm:w-10">
                <SparklesIcon className="h-6 w-6 text-indigo-600" aria-hidden="true" />
              </div>
              <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                <DialogTitle as="h3" className="text-base font-semibold leading-6 text-gray-900">
                  Welcome to Neoface.ai
                </DialogTitle>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">
                    You're among the first to experience our new platform. Watch our quick guide to learn how to anonymize your content and get started with Neoface.
                  </p>
                </div>
              </div>
            </div>

            {/* Video Section */}
            <div className="mt-6">
              <div className="relative w-full rounded-lg overflow-hidden" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  src="https://www.youtube.com/embed/48jPON38LO0?rel=0"
                  title="How to use Neoface"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute top-0 left-0 w-full h-full rounded-lg"
                />
              </div>
              <p className="mt-2 text-sm text-gray-500 text-center">
                You can always find this video later in the Learning Center.
              </p>
            </div>

            {/* Beta Notice Section */}
            <div className="mt-6 space-y-4 rounded-lg bg-gray-50 p-4">
              <div className="flex items-start space-x-3">
                <BugAntIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-gray-900">Early Access Experience</h4>
                  <p className="text-sm text-gray-500">As an early access user, you might encounter occasional bugs or issues while we polish the experience.</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <ChatBubbleBottomCenterTextIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-gray-900">Your Feedback Matters</h4>
                  <p className="text-sm text-gray-500">Help us improve by reporting issues and sharing your suggestions through the feedback button in the app.</p>
                </div>
              </div>
            </div>

            <div className="mt-6 sm:mt-6 flex justify-center space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex justify-center rounded-md bg-indigo-600 px-6 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                Get Started
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

// Hook to manage welcome modal state
export const useWelcomeModal = () => {
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  useEffect(() => {
    // Check if the welcome modal has been shown before
    const hasSeenWelcome = localStorage.getItem(WELCOME_MODAL_KEY);

    if (!hasSeenWelcome) {
      setShowWelcomeModal(true);
      // Store that the user has seen the welcome modal
      localStorage.setItem(WELCOME_MODAL_KEY, 'true');
    }
  }, []);

  return {
    isOpen: showWelcomeModal,
    onClose: () => setShowWelcomeModal(false)
  };
};

export default WelcomeModal;